import React, { FC, useState, useEffect } from 'react';
import { Box, Chip, Divider, Switch, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { OrderQuery } from '../../../../services/query';
import { OrderCommand } from '../../../../services/command';
import Swal from 'sweetalert2';
import { SM_GOAR } from './getOrderActionRules.classes';

const BadgeChip: FC<{ label: string }> = ({ label }) => (
    <Chip
        label={label}
        size="small"
        sx={{
            bgcolor: '#0039a6',
            color: 'white',
            fontWeight: 700,
            fontSize: 11,
            borderRadius: 1,
            width: 48,
            mr: 2,
            flexShrink: 0,
            '& .MuiChip-label': { px: 0.5 },
        }}
        className={SM_GOAR.toggleRowBadge}
    />
);

const ToggleRow: FC<{
    label: string;
    badge?: string;
    checked?: boolean;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}> = ({ label, badge, checked, onChange }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 52 }} className={SM_GOAR.toggleRow}>
        <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }} className={SM_GOAR.toggleRowLabel}>
            {label}
        </Typography>
        {badge && <BadgeChip label={badge} />}
        <Switch checked={!!checked} onChange={onChange} className={SM_GOAR.toggleRowToggle} />
    </Box>
);

const SwitchPair: FC<{
    leftLabel: string;
    leftChecked: boolean;
    onLeftChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    rightLabel: string;
    rightChecked: boolean;
    onRightChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}> = ({ leftLabel, leftChecked, onLeftChange, rightLabel, rightChecked, onRightChange }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 4, pl: 0.5, minHeight: 44 }} className={SM_GOAR.switchPair}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} className={SM_GOAR.switchPairSide}>
            <Typography variant="body2" color="text.secondary" className={SM_GOAR.switchPairLabel}>
                {leftLabel}
            </Typography>
            <Switch checked={leftChecked} onChange={onLeftChange} size="small" className={SM_GOAR.switchPairToggle} />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} className={SM_GOAR.switchPairSide}>
            <Typography variant="body2" color="text.secondary" className={SM_GOAR.switchPairLabel}>
                {rightLabel}
            </Typography>
            <Switch checked={rightChecked} onChange={onRightChange} size="small" className={SM_GOAR.switchPairToggle} />
        </Box>
    </Box>
);

export const GetOrderActionRules: FC = () => {
    const { t } = useTranslation();
    const [lCWCanceledForApproved, setLCWCanceledForApproved] = useState(false);
    const [lCWCanceledForPreparing, setLCWCanceledForPreparing] = useState(false);
    const [mpCanceledForPreparing, setMpCanceledForPreparing] = useState(false);
    const [mpCanceledForApproved, setMpCanceledForApproved] = useState(false);
    const [refresh, setRefresh] = React.useState(false);
    const [estimatedShippedDateByRule, setEstimatedShippedDateByRule] = useState<any>();
    const refreshFunc = () => setRefresh((prevRefreshing) => !prevRefreshing);

    const fetchOrderActionRules = () => {
        OrderQuery.GetOrderActionRules()
            .then((res: any) => {
                if (res) {
                    if (res.length > 0) {
                        setLCWCanceledForApproved(
                            res.find((r: any) => r.key === 'LCWCanceledForApproved') !== undefined
                        );
                        setLCWCanceledForPreparing(
                            res.find((r: any) => r.key === 'LCWCanceledForPreparing') !== undefined
                        );
                        setMpCanceledForPreparing(
                            res.find((r: any) => r.key === 'MPCanceledForPreparing') !== undefined
                        );
                        setMpCanceledForApproved(res.find((r: any) => r.key === 'MPCanceledForApproved') !== undefined);
                    } else {
                        setLCWCanceledForApproved(false);
                        setLCWCanceledForPreparing(false);
                        setMpCanceledForPreparing(false);
                        setMpCanceledForApproved(false);
                    }
                }
            })
            .catch((error: any) => {
                console.log('Hata :>> ', error);
            });
    };

    const fetchEstimatedShippedDateByRule = () => {
        OrderCommand.EstimatedShippedDateByRule({}).then((res: any) => {
            if (res) setEstimatedShippedDateByRule(res);
        });
    };

    const confirmAndRun = (action: () => Promise<void>) => {
        Swal.fire({
            text: t('authorizationManagement.listUser.surePassiveText'),
            cancelButtonText: t('general.cancel'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: t('general.update'),
        }).then(async (result: any) => {
            if (result.isConfirmed) {
                try {
                    await action();
                    Swal.fire(t('general.updated'), ' ', 'success');
                    refreshFunc();
                } catch (error) {
                    console.log('Hata :>> ', error);
                }
            }
        });
    };

    const handleToggleRule = (event: React.ChangeEvent<HTMLInputElement>, key: string) => {
        confirmAndRun(() =>
            OrderCommand.UpdateOrderActionRules({ isActive: !event.target.checked, key }).then(() => {})
        );
    };

    const handleChangeLCW = (event: React.ChangeEvent<HTMLInputElement>, id?: any, ruleTemplateId?: any) => {
        confirmAndRun(() =>
            OrderCommand.UpdateEstimatedShippedDateForLCWByRule({
                action: !event.target.checked,
                ruleTemplateId,
                ...(id ? { Id: id } : {}),
            }).then(() => fetchEstimatedShippedDateByRule())
        );
    };

    const handleChangeMP = (event: React.ChangeEvent<HTMLInputElement>, id?: any, ruleTemplateId?: any) => {
        confirmAndRun(() =>
            OrderCommand.UpdateEstimatedShippedDateForMPByRule({
                action: !event.target.checked,
                ruleTemplateId,
                ...(id ? { Id: id } : {}),
            }).then(() => fetchEstimatedShippedDateByRule())
        );
    };

    const handleChangeDeliveryMP = (event: React.ChangeEvent<HTMLInputElement>, id?: any, ruleTemplateId?: any) => {
        confirmAndRun(() =>
            OrderCommand.UpdateEstimatedDeliveryDateEnabledForMP({
                action: !event.target.checked,
                ruleTemplateId,
                ...(id ? { Id: id } : {}),
            }).then(() => fetchEstimatedShippedDateByRule())
        );
    };

    useEffect(() => {
        const timer = setTimeout(() => fetchOrderActionRules(), 1000);
        return () => clearTimeout(timer);
    }, [refresh]);

    useEffect(() => {
        fetchEstimatedShippedDateByRule();
    }, []);

    return (
        <Box
            sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                bgcolor: 'background.paper',
                px: 3,
                py: 1,
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                maxWidth: 560,
                width: '100%',
            }}
            className={SM_GOAR.card}
        >
            {/* Estimated Delivery Date — MP */}
            <ToggleRow
                label={t('general.estimatedDelivery')}
                badge="MP"
                checked={estimatedShippedDateByRule?.estimatedDeliveryDateEnabledForMP}
                onChange={(e) =>
                    handleChangeDeliveryMP(
                        e,
                        estimatedShippedDateByRule?.estimatedDeliveryDateEnabledForMPId,
                        estimatedShippedDateByRule?.ruleTemplateIdEstimatedDeliveryDateEnabledForMP
                    )
                }
            />

            <Divider className={SM_GOAR.divider} />

            {/* Estimated Shipped Date — LCW */}
            <ToggleRow
                label={t('serviceManagement.expectedShippingDate')}
                badge="LCW"
                checked={estimatedShippedDateByRule?.estimatedShippedDateForLCW}
                onChange={(e) =>
                    handleChangeLCW(
                        e,
                        estimatedShippedDateByRule?.estimatedShippedDateForLCWId,
                        estimatedShippedDateByRule?.ruleTemplateIdLCW
                    )
                }
            />

            <Divider className={SM_GOAR.divider} />

            {/* Estimated Shipped Date — MP */}
            <ToggleRow
                label={t('serviceManagement.expectedShippingDate')}
                badge="MP"
                checked={estimatedShippedDateByRule?.estimatedShippedDateForMP}
                onChange={(e) =>
                    handleChangeMP(
                        e,
                        estimatedShippedDateByRule?.estimatedShippedDateForMPId,
                        estimatedShippedDateByRule?.ruleTemplateIdMP
                    )
                }
            />

            <Divider className={SM_GOAR.divider} />

            {/* MP — Cancel Order */}
            <Box sx={{ pt: 1.5, pb: 0.5 }} className={SM_GOAR.group}>
                <Typography
                    variant="caption"
                    fontWeight={700}
                    color="text.secondary"
                    sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
                    className={SM_GOAR.groupTitle}
                >
                    MP – {t('serviceManagement.cancelOrder')}
                </Typography>
                <SwitchPair
                    leftLabel={t('general.approved')}
                    leftChecked={mpCanceledForApproved}
                    onLeftChange={(e) => handleToggleRule(e, 'MPCanceledForApproved')}
                    rightLabel={t('general.gettingReady')}
                    rightChecked={mpCanceledForPreparing}
                    onRightChange={(e) => handleToggleRule(e, 'MPCanceledForPreparing')}
                />
            </Box>

            <Divider className={SM_GOAR.divider} />

            {/* LCW — Cancel Order */}
            <Box sx={{ pt: 1.5, pb: 0.5 }} className={SM_GOAR.group}>
                <Typography
                    variant="caption"
                    fontWeight={700}
                    color="text.secondary"
                    sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
                    className={SM_GOAR.groupTitle}
                >
                    LCW – {t('serviceManagement.cancelOrder')}
                </Typography>
                <SwitchPair
                    leftLabel={t('general.approved')}
                    leftChecked={lCWCanceledForApproved}
                    onLeftChange={(e) => handleToggleRule(e, 'LCWCanceledForApproved')}
                    rightLabel={t('general.gettingReady')}
                    rightChecked={lCWCanceledForPreparing}
                    onRightChange={(e) => handleToggleRule(e, 'LCWCanceledForPreparing')}
                />
            </Box>
        </Box>
    );
};
